import Vue from "vue";
import Component from "vue-class-component";
import "@/libs/GeoUtils";
import "@/styles/bmap.scss";
import { SAMPLE_STATUS_DICT } from "../../../shared/constants/sample-status";
import { SampleStatus } from "../../../service/src/modules/sample/enums/sample-status.enum";

@Component
export default class BMapMixin extends Vue {
  /**
   * 加载百度地图
   * @returns
   */
  public async loadBmap(): Promise<any> {
    const {
      VUE_APP_BMAP_AK,
      VUE_APP_BMAP_API_URL,
      VUE_APP_BMAP_VERSION,
    } = process.env;
    if (!VUE_APP_BMAP_API_URL || !VUE_APP_BMAP_AK || !VUE_APP_BMAP_VERSION) {
      console.log("缺少环境变量");
      return;
    }
    // let src = VUE_APP_BMAP_API_URL;
    // if (src.split("").reverse()[0] === "/") src = src.replace(/^(.*)\/$/, "$1");
    if ((<any>window)["__initialize_bmap"] || (<any>window)["__bamp_ready"]) {
      return (<any>window)["BMap"];
    }
    (<any>window)["__initialize_bmap"] = () => {
      (<any>window)["__bamp_ready"] = true;
    };

    const script = document.createElement("script");
    const src = `${VUE_APP_BMAP_API_URL}?v=${VUE_APP_BMAP_VERSION}&ak=${VUE_APP_BMAP_AK}&callback=__initialize_bmap`;
    script.src = src;
    document.body.appendChild(script);

    return new Promise((resolve, reject) => {
      const loop_gap = 100;
      const timeout = 5000;
      this.$nextTick(() => {
        let _time = 0;
        const _i = setInterval(async () => {
          _time += loop_gap;
          if ((<any>window)["__bamp_ready"]) {
            resolve((<any>window)["BMap"]);
            clearInterval(_i);
          } else if (_time >= timeout) {
            reject("加载地图超时");
          }
        }, loop_gap);
      });
    });
  }

  /**
   * 根据点的列表计算中心坐标以及合适的缩放
   * @param points
   * @param domSelector
   * @returns
   */
  public calculatePointsCenterAndScale(
    points: Array<[number, number]>,
    domSelector: string
  ): {
    center: [number, number];
    scale: number;
  } {
    // console.log('cals',points)
    const info = points.reduce(
      (res: any, point: any): any => {
        const max = [
          Math.max(res.max[0], point[0]),
          Math.max(res.max[1], point[1]),
        ];
        const min = [
          Math.min(res.min[0], point[0]),
          Math.min(res.min[1], point[1]),
        ];
        return { max, min };
      },
      {
        max: [-Infinity, -Infinity],
        min: [Infinity, Infinity],
      }
    );
    const center = this.getGeoPointsCenter([info.max, info.min]);
    // const avrage_center = info.sum.map((v: any) => v / points.length);
    const x = (<any>window).BMapLib.GeoUtils.getDistance(
      new (<any>window)["BMap"].Point(info.max[0], center[1]),
      new (<any>window)["BMap"].Point(info.min[0], center[1])
    );
    const y = (<any>window).BMapLib.GeoUtils.getDistance(
      new (<any>window)["BMap"].Point(center[0], info.max[1]),
      new (<any>window)["BMap"].Point(center[0], info.min[1])
    );
    const largeSide = Math.max(x, y);
    const rect = document.querySelector(domSelector)?.getBoundingClientRect();
    const scale = Math.floor(
      18 -
        Math.log(largeSide / Math.min(rect?.width || 0, rect?.height || 0)) /
          Math.log(2)
    );

    return {
      center: <[number, number]>center,
      scale: Math.max(1, Math.min(14, scale)),
    };
  }

  public getGeoPointsCenter(points: any) {
    const total = points.length;
    let _x = 0,
      _y = 0,
      _z = 0;
    points.forEach((point: any) => {
      let lat, lng, x, y, z;
      lng = (point[0] * Math.PI) / 180;
      lat = (point[1] * Math.PI) / 180;
      x = Math.cos(lat) * Math.cos(lng);
      y = Math.cos(lat) * Math.sin(lng);
      z = Math.sin(lat);
      _x += x;
      _y += y;
      _z += z;
    });
    _x /= total;
    _y /= total;
    _z /= total;
    const _lng = Math.atan2(_y, _x);
    const _hyp = Math.sqrt(_x * _x + _y * _y);
    const _lat = Math.atan2(_z, _hyp);
    return [(_lng * 180) / Math.PI, (_lat * 180) / Math.PI];
  }

  public showTooltipOfPointCollection(map: any, event: any) {
    const { point } = event;
    const { sample } = point;
    const { detail } = sample;
    let tip = this.$el.querySelector("#_bmap_cus_point_collection_tooltip");
    if (!tip) {
      tip = document.createElement("div");
      tip.setAttribute("id", "_bmap_cus_point_collection_tooltip");
      this.$el.appendChild(tip);
    }
    // 清空
    tip.innerHTML = "";
    const title = document.createElement("div");
    title.classList.add("__title");
    const caption = document.createElement("div");
    caption.classList.add("text-grey");
    caption.classList.add("text-caption");

    title.innerText = detail.name;
    caption.innerText = detail.sample_id;

    tip.appendChild(title);
    tip.appendChild(caption);
    tip.classList.add("_show_tip");
    const pos = map.pointToPixel(point);
    tip.setAttribute(
      "style",
      `
        z-index: 9999;
        left: ${pos.x}px;
        top: ${pos.y}px;
        transform: translate(-50%, calc(-100% - 20px));
      `
    );
  }

  public hideTooltipOfPointCollection(event: any) {
    const tip = this.$el.querySelector("#_bmap_cus_point_collection_tooltip");
    if (!tip) return;
    tip.classList.remove("_show_tip");
  }

  public async renderSamplesGroupByStatus(
    samples: Array<any>,
    locationPrefix: string,
    targetId: string,
    callback?: (bmapPointCollection: any) => any
  ) {
    const lng_key = `${locationPrefix}_lon`;
    const lat_key = `${locationPrefix}_lat`;
    const filteredSamples = samples.filter((sample) => {
      return (
        sample.detail[lng_key] !== undefined &&
        sample.detail[lat_key] !== undefined &&
        sample.detail[lng_key] !== null &&
        sample.detail[lat_key] !== null
      );
    });
    if (!filteredSamples || !filteredSamples.length) return;
    const BMap = await this.loadBmap();
    this.$nextTick(() => {
      this.addVisualMap();
      const map = new BMap.Map(targetId);
      map.enableScrollWheelZoom(true);
      const { center, scale } = this.calculatePointsCenterAndScale(
        filteredSamples.map((sample) => [
          sample.detail[lng_key],
          sample.detail[lat_key],
        ]),
        `#${targetId}`
      );
      map.centerAndZoom(new BMap.Point(...center), scale);
      const groupedSamples = filteredSamples.reduce((dict, sample) => {
        const status = sample.sample_status;
        if (!dict[status]) dict[status] = [];
        dict[status].push(sample);
        return dict;
      }, {});

      const groupedStatus = Object.keys(groupedSamples);
      groupedStatus.sort((s1: string, s2: string) => {
        const _s1 = SAMPLE_STATUS_DICT[<SampleStatus>parseInt(s1)];
        const _s2 = SAMPLE_STATUS_DICT[<SampleStatus>parseInt(s2)];
        return (_s2?.order || Infinity) - (_s1?.order || Infinity);
      });

      groupedStatus.forEach((statusCode) => {
        const { color } = SAMPLE_STATUS_DICT[
          <SampleStatus>parseInt(statusCode)
        ];
        const samplesInGroup = groupedSamples[statusCode];
        const points: any[] = [];

        samplesInGroup.forEach((sample: any) => {
          const bmapPoint = new BMap.Point(
            sample.detail[lng_key],
            sample.detail[lat_key]
          );
          bmapPoint.sample = sample;
          points.push(bmapPoint);
        });

        const bmapPointCollection = new BMap.PointCollection(points, {
          size: (<any>window)["BMAP_POINT_SIZE_SMALL"],
          shape: (<any>window)["BMAP_POINT_SHAPE_CIRCLE"],
          color,
        });
        bmapPointCollection.addEventListener("mouseover", (e: any) => {
          this.showTooltipOfPointCollection(map, e);
        });
        bmapPointCollection.addEventListener("mouseout", (e: any) => {
          this.hideTooltipOfPointCollection(e);
        });
        callback && callback(bmapPointCollection);
        map.addOverlay(bmapPointCollection);
      });
      return true;
    });
  }

  public addVisualMap() {
    const status: SampleStatus[] = Object.keys(SAMPLE_STATUS_DICT).map((key) =>
      parseInt(key)
    );
    let visualMap = this.$el.querySelector("#_sample_status_visualmap");
    if (visualMap) this.$el.removeChild(visualMap);

    visualMap = document.createElement("div");
    visualMap.setAttribute("id", "_sample_status_visualmap");
    status.forEach((stat) => {
      const { color, description } = SAMPLE_STATUS_DICT[stat];
      const statDom = document.createElement("div");
      statDom.classList.add("sample-status-visual");
      const colorDom = document.createElement("div");
      colorDom.style.background = color;
      const textDom = document.createElement("div");
      textDom.innerHTML = description;
      statDom.appendChild(colorDom);
      statDom.appendChild(textDom);
      visualMap?.appendChild(statDom);
    });
    this.$el.appendChild(visualMap);
  }
}
